import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { mainPaymentMethodIdSchema } from '@cel_cash/core/schemas'
import { Subscription } from '@cel_cash/core/types'
import { RadioGroup, RadioGroupItem } from '@/global/components/RadioGroup'
import dayjs from 'dayjs'
import Dialog from 'global/guides/Dialog'
import { periodicidade } from 'locales/plan'
import { usePlanosContext } from 'pages/Planos.context'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { assinaturas, PlanoFixo } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { match } from 'ts-pattern'
import { z } from 'zod'
import AbasFormaPagamento from './forms/Pagamento/FormaPagamentoAbas'
import usePlanoByParam from './hooks/usePlanoByParam'

export default function AlterarPlano() {
    const { assinatura, cliente, PlanoDiferenciado } = usePlanosContext()
    const { mutateAsync, isLoading } =
        assinaturas.alteracao.mutations.useCreate({
            onSuccess: async () => {
                await assinaturas.minhaAssinatura.invalidateQueries()
                toast.success('Plano alterado com sucesso')
            },
            onError: e => {
                toast.error(e.message)
            },
        })

    const plano = usePlanoByParam()
    const navigate = useNavigate()

    if (!plano || !assinatura) return null

    return (
        <Dialog.Root
            defaultValues={{
                quando: 'agora',
                Plan: plano,
                Customer: cliente,
                mainPaymentMethodId: assinatura.mainPaymentMethodId,
            }}
            schema={z.object({
                quando: z.string(),
                mainPaymentMethodId: mainPaymentMethodIdSchema,
                Card: z.any(),
            })}
            onSubmit={async ({ Card, quando, mainPaymentMethodId }) => {
                return mutateAsync({
                    mainPaymentMethodId,
                    periodicity: plano.periodicity,
                    limiteFuncionarios: plano.limiteFuncionarios,
                    agora: quando === 'agora',
                    ...(mainPaymentMethodId === 'creditcard' && {
                        PaymentMethodCreditCard: { Card },
                    }),
                }).then(() => navigate('/planos/me'))
            }}
            loading={isLoading}
        >
            <Dialog.Title>Alterar plano</Dialog.Title>
            <Dialog.Content>
                <DiffPlanos
                    plano={{
                        ...plano,
                        limiteFuncionarios:
                            PlanoDiferenciado?.limiteFuncionarios ||
                            plano.limiteFuncionarios,
                    }}
                    assinatura={assinatura}
                />
                <RadioQuando assinatura={assinatura} />
                <HorizontalDivider className="my-8">
                    Forma de pagamento
                </HorizontalDivider>
                <AbasFormaPagamento />
            </Dialog.Content>
            <Dialog.Actions>
                {isLoading && <LoadingAnimatedTypography />}
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit>
                    Confirmar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function LoadingAnimatedTypography() {
    const messages = [
        '',
        'Aguarde um momento...',
        'Estamos processando seu pagamento',
        'Isso pode demorar um pouco',
        'Estamos quase lá',
        '',
    ]

    const [message, setMessage] = useState(messages[0])
    const [opacity, setOpacity] = useState(1)

    useMemo(() => {
        const interval = setInterval(async () => {
            setOpacity(0)
            await new Promise(r => setTimeout(r, 400))
            setMessage(prev => {
                let i = messages.indexOf(prev)
                i = i === messages.length - 1 ? -1 : i + 1
                if (i === -1) {
                    clearInterval(interval)
                    return messages[0]
                }
                return messages[i]
            })
            setOpacity(1)
        }, 6500)
        return () => clearInterval(interval)
    }, [])

    return (
        <span
            className="text-xs text-muted-foreground text-center block w-full"
            style={{
                opacity,
                transition: 'opacity 0.3s',
            }}
        >
            {message}
        </span>
    )
}

function DiffPlanos({
    plano,
    assinatura,
}: {
    plano: PlanoFixo
    assinatura: Subscription
}) {
    const cliente = useStore(state => state.customer)

    const planoAtual = {
        periodicity: periodicidade[assinatura.periodicity],
        limite: cliente?.limiteFuncionarios,
        valor: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(assinatura?.value / 100),
    }

    const novoPlano = {
        periodicity: periodicidade[plano.periodicity],
        limite: plano.limiteFuncionarios,
        valor: new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(+plano.price / 100),
    }

    return (
        <div className="h-fit">
            <ContentGuide.DataGrid
                loading={false}
                columns={[
                    {
                        ...optionsColumn,
                        field: 'id',
                        width: 200,
                    },
                    {
                        ...optionsColumn,
                        field: 'planoAtual',
                        headerName: 'Plano atual',
                        flex: 1,
                    },
                    {
                        ...optionsColumn,
                        field: 'novoPlano',
                        headerName: 'Novo plano',
                        flex: 1,
                    },
                ]}
                rows={[
                    {
                        id: 'Limite de funcionários',
                        planoAtual: `${planoAtual.limite}`,
                        novoPlano: `${novoPlano.limite}`,
                    },
                    {
                        id: 'Periodicidade',
                        planoAtual: `${planoAtual.periodicity}`,
                        novoPlano: `${novoPlano.periodicity}`,
                    },
                    {
                        id: 'Preço',
                        planoAtual: `${planoAtual.valor}`,
                        novoPlano: `${novoPlano.valor}`,
                    },
                ]}
                hideFooter
                headerHeight={40}
                density="compact"
                ToolbarProps={{
                    hideToolbar: true,
                }}
            />
        </div>
    )
}

function RadioQuando({ assinatura }: { assinatura: Subscription }) {
    const { watch, setValue } = useFormContext()
    const quando = watch('quando')

    let proximoPagamento = dayjs(assinatura.firstPayDayDate)
    const agora = dayjs()
    const toAdd = match(assinatura.periodicity)
        .with('biannual', () => [2, 'years'])
        .with('yearly', () => [1, 'year'])
        .with('quarterly', () => [3, 'months'])
        .with('bimonthly', () => [2, 'months'])
        .with('monthly', () => [1, 'month'])
        .with('biweekly', () => [2, 'weeks'])
        .with('weekly', () => [1, 'week'])
        .exhaustive() as [number, dayjs.ManipulateType]
    while (proximoPagamento.isBefore(agora)) {
        proximoPagamento = proximoPagamento.add(...toAdd)
    }

    return (
        <Grid container className="mt-4">
            <Grid item xs={12}>
                <span className="text-base">
                    Quando você deseja fazer a alteração?
                </span>
            </Grid>
            <Grid item xs={12}>
                <RadioGroup
                    name="quando"
                    onValueChange={valor => setValue('quando', valor)}
                    className="flex flex-row items-center gap-4"
                    value={quando}
                >
                    <RadioGroupItem value="agora" label="Agora" />
                    <RadioGroupItem
                        value="fatura"
                        label="No próximo ciclo de faturamento"
                    />
                </RadioGroup>
            </Grid>
            <Grid item xs={12}>
                {quando === 'fatura' && (
                    <span className="text-xs">
                        A alteração será realizada no próximo ciclo de
                        faturamento, ou seja, a partir do dia{' '}
                        {proximoPagamento.format('DD/MM/YYYY')}. E enquanto
                        isso, você continuará usufruindo do seu plano atual.
                    </span>
                )}
                {quando === 'agora' && (
                    <span className="text-xs">
                        A alteração será realizada imediatamente. Seu plano
                        atual será cancelado e o novo plano será ativado, mas
                        você só poderá utilizar o sistema após a confirmação do
                        pagamento e não será emitido reembolso do valor pago
                        pelo plano atual.
                    </span>
                )}
            </Grid>
        </Grid>
    )
}
